// Cognito Login
// --------------
// This is in effect the home screen
// For Cognito login details refer to https://aws-amplify.github.io/docs/js/authentication
//

// React imports
import { Amplify, Auth } from "aws-amplify";

import { clearPresignedImageCache } from "@/utils/presignedImageCache";

// Classes import
import {
  EMPTY_STRING,
  MFA_SETUP,
  MFA_TOTP_ISSUER,
  NEW_PASSWORD_REQUIRED,
  PASSWORD_RESET_REQUIRED_ERROR,
  SOFTWARE_TOKEN_MFA,
} from "../constants/definitions";
import { AxiosError } from "axios";

// The error happens if the user didn't finish the confirmation step when signing up
// In this case you need to resend the code and confirm the user
// About how to resend the code and confirm the user, please check the signUp part
const USER_NOT_CONFIRMED_ERROR = "User not confirmed";
// The error happens when the incorrect username/email, password or other error is provided
export const NOT_AUTHORIZED_ERROR = "Invalid credentials";
export const NOT_ABLE_TO_CHANGE_PASSWORD_ERROR = "Unable to change passphrase";
export const NOT_ABLE_TO_CONFIRM_ACCOUNT_ERROR = "Unable to confirm account";
export const NOT_ABLE_TO_RESET_PASSWORD_ERROR = "Unable to reset passphrase";
// Shown when the entered TOTP code is wrong/expired during an MFA challenge.
export const INVALID_MFA_CODE_ERROR = "Invalid or expired code. Please try again.";
// The pool is TOTP-only. If Cognito ever issues an SMS challenge (misconfiguration),
// fail loudly rather than silently treating it as a successful sign-in.
export const MFA_METHOD_NOT_SUPPORTED_ERROR =
  "This account requires an authenticator app. Contact your administrator.";

interface CognitoUser {
  challengeName?: string;
  challengeParam?: any;
  [key: string]: any;
}

interface CognitoError {
  code: string;
  message: string;
  [key: string]: any;
}

export class CognitoLoginClass {
  private currentuser: CognitoUser | null;
  private authenticated: boolean;
  // True while a login has produced an unresolved TOTP challenge (the user has
  // supplied a valid password but not yet a valid second factor). Set when
  // login()/activateAccount() surface SOFTWARE_TOKEN_MFA or MFA_SETUP; cleared
  // when the challenge is satisfied or the user logs out. The /mfa page reads it
  // (via isMfaPending) so a hard refresh mid-challenge — which drops the
  // in-memory challenge user — bounces back to login instead of dead-ending.
  private mfaPending: boolean;

  // Constructor
  // ------------
  //
  constructor() {
    this.currentuser = null;
    this.authenticated = false;
    this.mfaPending = false;
  }

  // Configure
  // ----------
  // Configure amplify
  //
  configure(
    region: string,
    userpoolid: string,
    appclientid: string,
    identityPoolId: string
  ): void {
    // Configure Amplify with Cognito User Pools (JWT) as the DEFAULT auth, plus
    // the Identity Pool solely to obtain GUEST credentials for ONE thing: the
    // @aws_iam requestNewInvite recovery mutation, which the logged-out /welcome
    // page calls as an identity-pool guest (authMode "AWS_IAM").
    //
    // This was previously omitted for fear it would force sigv4 on every call and
    // break tenant-scoped resolvers. It does not: the API default auth mode is
    // pinned to AMAZON_COGNITO_USER_POOLS in setGraphDetails
    // (aws_appsync_authenticationType), so EVERY authenticated GraphQL call —
    // explicit authMode or default — resolves to the JWT and keeps its
    // custom:tenantId claim. Guest creds are used ONLY by the single call that
    // explicitly passes authMode "AWS_IAM". The per-client identity-pool UNAUTH
    // role grants appsync:GraphQL on that one field ARN and nothing else.
    Amplify.configure({
      Auth: {
        region: region,
        userPoolId: userpoolid,
        userPoolWebClientId: appclientid,
        identityPoolId: identityPoolId,
        authenticationFlowType: "USER_SRP_AUTH",
      },
    });
  }

  // Login
  // ------
  //
  async login(username: string, password: string): Promise<string> {
    let returnMessage = EMPTY_STRING;

    try {
      // Try to sign in
      const user = await Auth.signIn(username, password);

      if (NEW_PASSWORD_REQUIRED === user.challengeName) {
        // New password required happens when a new user account is created.
        // Retain the challenge user so the activation flow can complete it.
        this.currentuser = user;
        this.authenticated = true;
        returnMessage = NEW_PASSWORD_REQUIRED;
      } else if (user.challengeName === SOFTWARE_TOKEN_MFA) {
        // Returning user with TOTP enrolled — must supply a 6-digit code before
        // a session is issued. Keep the challenge user for confirmMfaCode().
        this.currentuser = user;
        this.mfaPending = true;
        returnMessage = SOFTWARE_TOKEN_MFA;
      } else if (user.challengeName === MFA_SETUP) {
        // MFA is required but this user has no factor yet — they must enrol a
        // TOTP authenticator now. Keep the challenge user for the setup calls.
        this.currentuser = user;
        this.mfaPending = true;
        returnMessage = MFA_SETUP;
      } else if (user.challengeName === "SMS_MFA") {
        // TOTP-only pool: an SMS challenge means a misconfiguration. Fail loudly
        // instead of falling through to the "signed in" branch below.
        returnMessage = MFA_METHOD_NOT_SUPPORTED_ERROR;
      } else {
        // The user directly signs in - no challenge
        returnMessage = EMPTY_STRING;
        // Set the user value
        this.currentuser = user;
        this.authenticated = true;
      }
    } catch (err) {
      const error = err as CognitoError;
      if (error.code === "UserNotConfirmedException") {
        returnMessage = USER_NOT_CONFIRMED_ERROR;
      } else if (error.code === PASSWORD_RESET_REQUIRED_ERROR) {
        // An admin reset the password in the Cognito console — the user must go
        // through the "forgot password" verification-code flow to set a new one.
        // Return the canonical exception code so Login.tsx routes to /forgot.
        returnMessage = PASSWORD_RESET_REQUIRED_ERROR;
      } else if (
        error.code === "NotAuthorizedException" ||
        error.code === "UserNotFoundException"
      ) {
        returnMessage = NOT_AUTHORIZED_ERROR;
      } else {
        returnMessage = NOT_AUTHORIZED_ERROR;
      }
    }

    return returnMessage;
  }

  // Confirm MFA Code
  // -----------------
  // Completes a SOFTWARE_TOKEN_MFA challenge for a returning, already-enrolled
  // user. Operates on the challenge user retained by login(). On success the
  // Cognito session is established and the user is authenticated.
  //
  async confirmMfaCode(code: string): Promise<string> {
    if (!this.currentuser) {
      return NOT_AUTHORIZED_ERROR;
    }
    try {
      const user = await Auth.confirmSignIn(
        this.currentuser,
        code,
        SOFTWARE_TOKEN_MFA
      );
      this.currentuser = user;
      this.authenticated = true;
      this.mfaPending = false;
      return EMPTY_STRING;
    } catch (err) {
      return INVALID_MFA_CODE_ERROR;
    }
  }

  // Begin TOTP Setup
  // -----------------
  // Starts enrolment for a user hit with the MFA_SETUP challenge (or a new user
  // right after completeNewPassword). Returns the shared secret and the otpauth
  // URI to render as a QR code. The secret is generated by Cognito and never
  // leaves the browser except back to Cognito on verification.
  //
  async beginTotpSetup(
    accountLabel: string
  ): Promise<{ secret: string; otpauthUri: string } | null> {
    if (!this.currentuser) {
      return null;
    }
    try {
      const secret = await Auth.setupTOTP(this.currentuser);
      const issuer = encodeURIComponent(MFA_TOTP_ISSUER);
      const account = encodeURIComponent(accountLabel || "account");
      const otpauthUri = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}`;
      return { secret, otpauthUri };
    } catch (err) {
      console.error("Failed to begin TOTP setup:", err);
      return null;
    }
  }

  // Confirm TOTP Setup
  // -------------------
  // Verifies the first code from the user's authenticator, which both finalises
  // the MFA_SETUP challenge (establishing the session) and pins TOTP as the
  // preferred second factor for subsequent logins.
  //
  async confirmTotpSetup(code: string): Promise<string> {
    if (!this.currentuser) {
      return NOT_AUTHORIZED_ERROR;
    }
    try {
      await Auth.verifyTotpToken(this.currentuser, code);
      // The session now exists. Pin TOTP as the preferred method so future
      // logins issue SOFTWARE_TOKEN_MFA rather than MFA_SETUP again. Guard its
      // errors: a hiccup here must not block a user who has already verified.
      try {
        await Auth.setPreferredMFA(this.currentuser, "TOTP");
      } catch (preferenceError) {
        console.error("Failed to set preferred MFA method:", preferenceError);
      }
      this.authenticated = true;
      this.mfaPending = false;
      return EMPTY_STRING;
    } catch (err) {
      return INVALID_MFA_CODE_ERROR;
    }
  }

  // Is MFA Pending
  // ---------------
  // True when a password has been accepted but the TOTP challenge is unresolved.
  // The /mfa page uses this to detect a stale/refreshed challenge and redirect
  // back to login rather than sitting on an unusable form.
  isMfaPending(): boolean {
    return this.mfaPending;
  }

  // Activate Account
  // -----------------
  // Required attributes are already provided by Admin:
  // * email
  // * given_name
  // * family_name
  // The optional attributes are:
  // * preferred_username
  // * picture
  //
  async activateAccount(
    username: string,
    oldpassword: string,
    newpassword: string
  ): Promise<string> {
    try {
      const signedInUser = await Auth.signIn(username, oldpassword);
      const completedUser = await Auth.completeNewPassword(
        signedInUser,
        newpassword
      );

      if (!completedUser) {
        return NOT_ABLE_TO_CONFIRM_ACCOUNT_ERROR;
      }

      // Under MfaConfiguration=REQUIRED, completeNewPassword resolves to a user
      // still carrying an MFA_SETUP challenge — the password is set but no
      // session exists yet. Surface MFA_SETUP so the caller routes to TOTP
      // enrolment, and keep the challenge user for beginTotpSetup/confirmTotpSetup.
      if (completedUser.challengeName === MFA_SETUP) {
        this.currentuser = completedUser;
        this.mfaPending = true;
        return MFA_SETUP;
      }

      // MFA disabled (or already enrolled): the session is established.
      this.currentuser = completedUser;
      this.authenticated = true;
      return EMPTY_STRING;
    } catch (err) {
      console.error("Failed to activate account:", err);
      return NOT_ABLE_TO_CONFIRM_ACCOUNT_ERROR;
    }
  }

  // Change Password
  // ---------------
  // This is a Cognito call
  // No graphql
  //
  async changePassword(
    oldpassphrase: string,
    newpassphrase: string
  ): Promise<string> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldpassphrase, newpassphrase);
      return EMPTY_STRING;
    } catch (err) {
      return NOT_ABLE_TO_CHANGE_PASSWORD_ERROR;
    }
  }

  // --- Self-service TOTP management (for the already-signed-in user) ----------
  // These differ from the login-challenge methods above: they operate on the
  // CURRENT authenticated user (a live session), not on a mid-login challenge
  // user. Used by the profile "Replace authenticator" flow so a user rotating
  // to a new phone doesn't need an admin reset.

  // Get Preferred MFA
  // ------------------
  // Returns the user's active MFA method ("SOFTWARE_TOKEN_MFA" once TOTP is
  // enrolled, "NOMFA" otherwise). Used to gate MFA UI: the profile shows the
  // "Replace authenticator" action only when TOTP is actually enrolled, which
  // also keeps it hidden while the pool has MFA switched off.
  async getPreferredMfa(): Promise<string> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      // bypassCache so a just-enrolled/just-reset preference is reflected.
      return await Auth.getPreferredMFA(user, { bypassCache: true });
    } catch {
      return "NOMFA";
    }
  }

  // Begin TOTP Re-enrollment
  // -------------------------
  // Generates a fresh TOTP secret for the signed-in user and returns the
  // otpauth URI to render as a QR code. Enrolling a new secret supersedes the
  // old device once confirmTotpReenrollment() verifies it.
  async beginTotpReenrollment(
    accountLabel: string
  ): Promise<{ secret: string; otpauthUri: string } | null> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const secret = await Auth.setupTOTP(user);
      const issuer = encodeURIComponent(MFA_TOTP_ISSUER);
      const account = encodeURIComponent(accountLabel || "account");
      const otpauthUri = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}`;
      return { secret, otpauthUri };
    } catch (err) {
      console.error("Failed to begin TOTP re-enrollment:", err);
      return null;
    }
  }

  // Confirm TOTP Re-enrollment
  // ---------------------------
  // Verifies a code from the new authenticator and pins TOTP as preferred,
  // completing the swap to the new device. The session is unaffected — the user
  // stays logged in.
  async confirmTotpReenrollment(code: string): Promise<string> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, code);
      await Auth.setPreferredMFA(user, "TOTP");
      return EMPTY_STRING;
    } catch (err) {
      return INVALID_MFA_CODE_ERROR;
    }
  }

  // Forgot Password
  // ----------------
  // Used to send the user a verification code
  //
  async forgotPassword(username: string) {
    try {
      await Auth.forgotPassword(username);
    } catch (error) {
      return NOT_ABLE_TO_CONFIRM_ACCOUNT_ERROR;
    }
    // Auth.forgotPassword(username)
    //   .then((data) => {
    //     // Nothing - no response to the user if it succeeded or not
    //   })
    //   .catch((err) => {
    //     // Nothing - no response to the user if it succeeded or not
    //   });
  }

  // Reset Password
  // ---------------
  // Used to complete the forgot password process
  //
  async resetPassword(
    username: string,
    verificationcode: string,
    newpassword: string
  ): Promise<string> {
    try {
      await Auth.forgotPasswordSubmit(username, verificationcode, newpassword);
      return EMPTY_STRING;
    } catch (err) {
      return (err as AxiosError).message || NOT_ABLE_TO_RESET_PASSWORD_ERROR;
    }
  }

  // Mark As Authenticated
  // ----------------------
  // Used to restore authenticated state when a valid Cognito session
  // is found on page refresh (tokens still in localStorage)
  //
  markAsAuthenticated(): void {
    this.authenticated = true;
  }

  // Is Authenticated
  // -----------------
  // Returns true if authenticated, false otherwise
  isAuthenticated(): boolean {
    return this.authenticated;
  }

  currentUser() {
    return this.currentuser;
  }

  // Get Tier
  // ---------
  // Returns the user's tier from the Cognito ID token ("enterprise" or "sme").
  // The custom:tier attribute is immutable and set at user creation — it cannot
  // be tampered with by the client.
  async getTier(): Promise<"enterprise" | "sme"> {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const tier = idToken.payload["custom:tier"];
      return tier === "sme" ? "sme" : "enterprise";
    } catch {
      return "enterprise";
    }
  }

  // Read the tenant identifier from the JWT. This is the partition key for
  // every per-tenant DDB table and the canonical client identity. Immutable
  // and server-signed — cannot be tampered with by the client.
  // Works for both Enterprise (tenantId = clientName, e.g. "tharwa") and
  // SME (tenantId = org identifier assigned at signup).
  async getTenantId(): Promise<string> {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      return idToken.payload["custom:tenantId"] || "";
    } catch {
      return "";
    }
  }

  // Logout
  // -------
  // By doing this, you are revoking all the auth tokens(id token, access token and refresh token)
  // which means the user is signed out from all the devices
  // Note: although the tokens are revoked, the AWS credentials will remain valid until they expire (which by default is 1 hour)
  //
  // Returns a Promise so callers can await completion before navigating away.
  // This prevents in-flight API.graphql() calls from receiving "No current user"
  // because Amplify internally calls Auth.currentSession() to attach a JWT,
  // and the session is cleared mid-flight when signOut resolves.
  logout(): Promise<void> {
    this.authenticated = false;
    this.mfaPending = false;
    this.currentuser = null;
    // Vulnerability screenshots are held as in-memory blobs so they outlive the
    // presigned URL that fetched them. Signing out is a client-side route
    // change, not a reload, so without this the departing user's evidence would
    // still be in memory for whoever signs in next on the same tab.
    clearPresignedImageCache();
    return Auth.signOut({ global: true }).catch(() => {
      // Swallow signOut errors — local session is cleared regardless of network errors
    });
  }
}

export default CognitoLoginClass;
